import { Injectable } from "@angular/core";
import { forkJoin, from, Observable, zip } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { TitleItem } from "../../_objects/title";
import { FILE_SYSTEM, IPC_CHANNEL } from "../../_offline/electron.constants";
import { ElectronService } from "../../electron.service";
import { FileSystemAccessService } from "../file-system-access.service";
import { MarksStateService } from "../mark-state/marks-state.service";
import { TitlesStateService } from "../title-state/titles-state.service";
import { HttpClient } from "@angular/common/http";
import { UnzipResult } from "./unzip-result";
import { OfflineUtilsService } from "../../_offline/services/offline-utils.service";
import { LogoImageOfflineService } from "../offline/logo-image-offline.service";

export type CompareImage = { imageName: string; isMeasureable: boolean; imageUrl: string; contentType: string };

@Injectable({
  providedIn: "root"
})
export abstract class DeviceDataExportService {
  abstract exportSynchronizedInspectionTemplateToBrian(blob: Blob, inspectionId: string, inspectionTitle: string): Promise<UnzipResult>;

  abstract exportPresetDataToDevice(): Observable<any>;
}

@Injectable({
  providedIn: "root"
})
export class BrianDeviceExportService extends DeviceDataExportService {
  private marksFileName = "MARK_SET.dat";
  private titleFileName = "TITLE.dat";
  private logoFileName = "LOGO.BMP";
  private stereoOpticalAdapterFileName = "__check_inspection.json";

  constructor(
    private marksStateService: MarksStateService,
    private titleStateService: TitlesStateService,
    private electronService: ElectronService,
    private fileSystemAccessService: FileSystemAccessService,
    private http: HttpClient,
    private logoService: LogoImageOfflineService,
    private offlineUtilsService: OfflineUtilsService
  ) {
    super();
  }

  getPathSeparator() {
    return this.fileSystemAccessService.getSystemPathSeparator();
  }

  public async exportSynchronizedInspectionTemplateToBrian(blob: Blob, inspectionId: string, inspectionTitle: string): Promise<UnzipResult> {
    const buffer = await blob.arrayBuffer();

    const result: UnzipResult = this.electronService.sendSyncCommand(
      IPC_CHANNEL.BRIAN.UNZIP_TO_BRIAN_INSPECTIONS_FOLDER,
      buffer,
      inspectionId,
      inspectionTitle
    );

    const { path, error } = result;

    return {
      inspectionId,
      path,
      error
    };
  }

  public exportPresetDataToDevice() {
    const writeMarks = this.marksStateService.markItems$.pipe(
      switchMap((marks) => this.createPresetFile(this.offlineUtilsService.generateMarksText(marks), this.marksFileName, FILE_SYSTEM.PATH.PRESET))
    );

    const writeTitles = this.titleStateService.titleItems$.pipe(
      switchMap((titles) => this.createPresetFile(this.titlesToText(titles), this.titleFileName, FILE_SYSTEM.PATH.PRESET))
    );

    const writeLogo = this.prepareAndExportLogo();

    return this.createPresetFolderIfNotExist().pipe(switchMap(() => zip(writeMarks, writeTitles, writeLogo)));
  }

  public exportCompareImageToDevice(compareImage: CompareImage) {
    const saveCompareImage = (base64Image: string) => {
      return this.getInspectionsFolderPaths().pipe(
        switchMap((folders) => forkJoin(folders.map((inspectionPath) => this.saveAsCompareImage(inspectionPath, compareImage, base64Image))))
      );
    };

    return this.getBlob(compareImage.imageUrl).pipe(
      switchMap((blob) => this.blobToBase64(blob)),
      map((base64) => base64.split(",")[1]),
      switchMap((base64Data) => saveCompareImage(base64Data))
    );
  }

  private saveAsCompareImage(inspectionPath: string, compareImage: CompareImage, base64Image: string) {
    const compareFolderPath = [inspectionPath, FILE_SYSTEM.PATH.COMPARE].join(this.getPathSeparator());
    const imagePath = [compareFolderPath, compareImage.imageName].join(this.getPathSeparator());
    const stereoOpticalAdapterFilePath = [compareFolderPath, this.stereoOpticalAdapterFileName].join(this.getPathSeparator());

    const stereoOpticalAdapter = { stereoOpticalAdapter: compareImage.isMeasureable ? 1 : 0 };

    return from(this.fileSystemAccessService.deleteFolder(compareFolderPath, true)).pipe(
      switchMap(() => this.electronService.sendCommand(IPC_CHANNEL.FILE.PUT_ONE, imagePath, base64Image, compareImage.contentType)),
      switchMap(() =>
        this.electronService.sendCommand(IPC_CHANNEL.FILE.POST_TEXT_FILE, stereoOpticalAdapterFilePath, JSON.stringify(stereoOpticalAdapter))
      )
    );
  }

  private getInspectionsFolderPaths(): Observable<string[]> {
    return from(this.electronService.invoke(IPC_CHANNEL.BRIAN.GET_ALL_INSPECTIONS_FOLDERS));
  }

  private titlesToText(titles: TitleItem[]): string {
    return titles.reduce((acc, item) => acc + item.identifier + "\r\n", "");
  }

  private createPresetFile(fileTextContent: string, fileName: string, folder: string) {
    return this.getIplexOneFolderPath(folder).pipe(
      switchMap((path) =>
        this.electronService.sendCommand(IPC_CHANNEL.FILE.POST_TEXT_FILE, [path, fileName].join(this.getPathSeparator()), fileTextContent)
      )
    );
  }

  private prepareAndExportLogo(): Observable<any> {
    return this.logoService.getLogoImage().pipe(
      switchMap((url) => {
        if (url === null) {
          return this.getLogoFolderPath().pipe(switchMap((logoFolder) => from(this.fileSystemAccessService.deleteFolder(logoFolder, true))));
        }
        return this.getBlob(url).pipe(
          switchMap((blob) => this.blobToBase64(blob)),
          map((base64) => base64.split(",")[1]),
          switchMap((base64Data) => this.exportLogo(base64Data))
        );
      })
    );
  }

  private exportLogo(base64Data: string): Observable<any> {
    return this.getLogoFolderPath().pipe(
      switchMap((path) => {
        const filePath = [path, this.logoFileName].join(this.getPathSeparator());
        return this.electronService.sendCommand(IPC_CHANNEL.FILE.PUT_ONE, filePath, base64Data, "image/png");
      })
    );
  }

  private getBlob(url: string): Observable<Blob> {
    return this.http.get<Blob>(url, { responseType: "blob" as "json" });
  }

  private blobToBase64(blob: Blob): Observable<string> {
    const reader = new FileReader();
    const result$ = new Observable<string>((observer) => {
      reader.onloadend = () => {
        const base64data = reader.result as string;
        observer.next(base64data);
        observer.complete();
      };
      reader.onerror = (error) => {
        observer.error(error);
      };
    });
    reader.readAsDataURL(blob);
    return result$;
  }

  private getIplexOneFolderPath(folder: string) {
    return from(this.fileSystemAccessService.getUserFolderPath()).pipe(
      map((folderPath) => [folderPath, FILE_SYSTEM.PATH.IPLEX_ONE, folder].join(this.getPathSeparator()))
    );
  }

  private getLogoFolderPath() {
    return from(this.fileSystemAccessService.getUserFolderPath()).pipe(
      map((folderPath) => [folderPath, FILE_SYSTEM.PATH.IPLEX_ONE, FILE_SYSTEM.PATH.LOGO].join(this.getPathSeparator()))
    );
  }

  private createPresetFolderIfNotExist() {
    return this.getIplexOneFolderPath(FILE_SYSTEM.PATH.PRESET).pipe(
      switchMap((path) => this.electronService.sendCommand(IPC_CHANNEL.FILE.CREATE_FOLDER, path))
    );
  }
}
